import { Button, SyntaxIcon } from '@expressable/syntax-ds';
import { CaretLeft } from '@phosphor-icons/react';
import { getTrackEventForScreenerQuestion } from 'domains/signup/data/mixpanelTracking';
import ScreenerQuestion from 'domains/signup/screener/Components/ScreenerQuestion';
import { ScreenerContext, withScreenerMachineState } from 'domains/signup/screener/context/screenerContext';
import { useScreenerResponses } from 'domains/signup/screener/context/useScreener';
import { isUndefined } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCounter } from 'react-use';
import { TypeTherapyScreenerV2 } from 'types/contentful';
import { useTrack } from 'utils/hooks/useMixPanel';

type Props = {
  screener: TypeTherapyScreenerV2;
  onSubmit: (responses: string[]) => void;
};

const RunScreener: React.FC<Props> = ({ onSubmit, screener }) => {
  const {
    fields: { questions },
  } = screener;

  const { responses, submitResponse } = useScreenerResponses();

  const [currentQuestionIndex, { dec, inc }] = useCounter(0);

  const currentQuestion = useMemo(() => questions?.[currentQuestionIndex], [questions, currentQuestionIndex]);

  const { track } = useTrack();

  const { send } = ScreenerContext.useActorRef();

  useEffect(() => {
    track(getTrackEventForScreenerQuestion(screener, currentQuestionIndex));
  }, [track, screener, currentQuestionIndex]);

  const { t } = useTranslation();

  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  useEffect(() => {
    send({ type: 'questionIndexChanged', index: currentQuestionIndex });
  }, [currentQuestionIndex, send]);

  if (!currentQuestion) {
    return null;
  }

  return (
    <>
      {currentQuestionIndex > 0 && (
        <Button variant="secondary" size="md" className="mb-24 font-semibold" onClick={() => dec()}>
          <SyntaxIcon icon={CaretLeft} />
          {t('screener.backButton')}
        </Button>
      )}
      <ScreenerQuestion
        key={currentQuestionIndex}
        question={currentQuestion}
        currentResponse={responses[currentQuestionIndex]}
        submitResponse={(response: string) => {
          submitResponse(currentQuestionIndex, response);
          if (!isLastQuestion) {
            inc();
          }
        }}
      />
      {isLastQuestion && (
        <Button
          className="mt-64 sm-d:w-full"
          size="lg"
          onClick={() => onSubmit(responses as string[])}
          disabled={isUndefined(responses[currentQuestionIndex])}
        >
          {t('screener.questions.finishButton')}
        </Button>
      )}

      {!isLastQuestion && !isUndefined(responses[currentQuestionIndex]) && (
        <Button
          className="mt-64 sm-d:w-full"
          size="lg"
          onClick={() => inc()}
          disabled={isUndefined(responses[currentQuestionIndex])}
        >
          {t('screener.questions.continueButton')}
        </Button>
      )}
    </>
  );
};

const ScreenerWrapper = () => {
  const therapyScreener = ScreenerContext.useSelector(
    ({ context }) => context.therapyScreener,
  ) as TypeTherapyScreenerV2;
  const { send } = ScreenerContext.useActorRef();

  const onSubmit = useCallback(
    (responses: string[]) => {
      send({ type: 'submitScreenerResponses', responses });
    },
    [send],
  );

  return <RunScreener onSubmit={onSubmit} screener={therapyScreener} />;
};

export const ScreenerRunningScreenerQuestions = withScreenerMachineState(ScreenerWrapper, 'runningScreenerQuestions');
